import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, setAuth } from "@/lib/api";
import { setActiveLearner } from "@/lib/learner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { ArrowRight, ShieldCheck, GraduationCap, Mail, UserRound, Building2, Lock, Eye, EyeOff, Handshake } from "lucide-react";

const OTP_RESEND_SECONDS = 30;

// Cheeky, brand-safe welcome messages shown via toast on successful sign-in.
const LOGIN_WELCOME_LINES = [
  (n) => `Welcome back, ${n} — let's make some sound decisions today 🎧`,
  (n) => `Good to see you, ${n}. Knowledge incoming 🚀`,
  (n) => `${n}, you're early! The quiet wins start here ✨`,
  (n) => `Signed in, ${n}. Let's tune up your selling 🔊`,
  (n) => `Back at it, ${n}? Future-you says thanks 🏆`,
  (n) => `Hello, ${n}. The Academy missed you 🎓`,
  (n) => `${n}, ready to crush another module? 💪`,
];
const pickWelcome = (name) => {
  const first = (name || "").split(" ")[0] || name || "there";
  const fn = LOGIN_WELCOME_LINES[Math.floor(Math.random() * LOGIN_WELCOME_LINES.length)];
  return fn(first);
};

export default function Login() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("admin");

  // Admin form state
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPassword, setAdminPassword] = useState("");

  // Learner auth state (email + password)
  const [learnerMode, setLearnerMode] = useState("signin"); // signin | signup
  const [learnerEmail, setLearnerEmail] = useState("");
  const [learnerName, setLearnerName] = useState("");
  const [learnerDept, setLearnerDept] = useState("");
  const [learnerPassword, setLearnerPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);

  const [loading, setLoading] = useState(false);
  const stageRef = useRef(null);

  // Dealer form state (email + password only, no self-serve signup)
  const [dealerEmail, setDealerEmail] = useState("");
  const [dealerPassword, setDealerPassword] = useState("");

  // Shared post-login: persist auth, attach the learner, route — including
  // the forced first-login password change for dealer accounts.
  const completeLogin = (data) => {
    // Manifesto must be re-read and confirmed on every login.
    try { sessionStorage.removeItem("uvc_manifesto_ack_session"); } catch (_) { /* noop */ }
    setAuth(data.token, {
      email: data.email,
      role: data.role,
      name: data.name,
      learner_id: data.learner_id,
      audience: data.audience || "employee",
      must_reset_password: !!data.must_reset_password,
    });
    if (data.role === "learner" && data.learner_id) {
      setActiveLearner({
        id: data.learner_id,
        name: data.name,
        email: data.email,
        department: "",
        audience: data.audience || "employee",
      });
    }
    if (data.must_reset_password) {
      toast.info("Please set your own password to continue.");
      navigate("/reset-password");
      return;
    }
    toast.success(pickWelcome(data.name));
    navigate(data.role === "admin" ? "/admin" : "/learn");
  };

  // Mouse parallax — updates --mx/--my CSS vars on the stage element
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;
    const handler = (e) => {
      const r = el.getBoundingClientRect();
      const mx = ((e.clientX - r.left) / r.width - 0.5) * 2;
      const my = ((e.clientY - r.top) / r.height - 0.5) * 2;
      el.style.setProperty("--mx", mx.toFixed(3));
      el.style.setProperty("--my", my.toFixed(3));
    };
    const reset = () => { el.style.setProperty("--mx", "0"); el.style.setProperty("--my", "0"); };
    el.addEventListener("mousemove", handler);
    el.addEventListener("mouseleave", reset);
    return () => { el.removeEventListener("mousemove", handler); el.removeEventListener("mouseleave", reset); };
  }, []);

  const onAdminSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email: adminEmail, password: adminPassword, remember_me: rememberMe });
      completeLogin(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const onDealerSubmit = async (e) => {
    e.preventDefault();
    if (!dealerEmail) return toast.error("Please enter your email");
    if (!dealerPassword) return toast.error("Please enter your password");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", {
        email: dealerEmail.toLowerCase().trim(),
        password: dealerPassword,
        remember_me: rememberMe,
      });
      completeLogin(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign in failed");
    } finally {
      setLoading(false);
    }
  };

  const onLearnerSubmit = async (e) => {
    e.preventDefault();
    if (!learnerEmail) return toast.error("Please enter your email");
    if (!learnerPassword) return toast.error("Please enter your password");
    if (learnerMode === "signup") {
      if (!learnerName.trim()) return toast.error("Please enter your name");
      if (learnerPassword.length < 8) return toast.error("Password must be at least 8 characters");
    }
    setLoading(true);
    try {
      const email = learnerEmail.toLowerCase().trim();
      const { data } = learnerMode === "signup"
        ? await api.post("/auth/signup", {
            name: learnerName.trim(),
            email,
            password: learnerPassword,
            department: learnerDept.trim(),
          })
        : await api.post("/auth/login", { email, password: learnerPassword, remember_me: rememberMe });
      if (learnerMode === "signup") {
        try { sessionStorage.removeItem("uvc_manifesto_ack_session"); } catch (_) { /* noop */ }
        setAuth(data.token, {
          email: data.email, role: data.role, name: data.name,
          learner_id: data.learner_id, audience: data.audience || "employee",
          must_reset_password: false,
        });
        setActiveLearner({
          id: data.learner_id, name: data.name, email: data.email,
          department: learnerDept.trim() || "", audience: data.audience || "employee",
        });
        toast.success(`Welcome aboard, ${(data.name || "").split(" ")[0] || data.name}! 🎉`);
        navigate(data.role === "admin" ? "/admin" : "/learn");
      } else {
        completeLogin(data);
      }
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409 && learnerMode === "signup") {
        toast.error("This email already has an account — sign in instead.");
        setLearnerMode("signin");
      } else if (status === 403 && learnerMode === "signup") {
        toast.error(typeof detail === "string" ? detail : "Sign-up is restricted to @united-group.in emails. Contact aiteam@united-group.in for access.", { duration: 8000 });
      } else if (status === 401 && learnerMode === "signin" && typeof detail === "string" && detail.includes("sign up")) {
        toast.error("This account has no password yet — sign up to set one.");
        setLearnerMode("signup");
      } else {
        toast.error(typeof detail === "string" ? detail : (learnerMode === "signup" ? "Sign up failed" : "Sign in failed"));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left visual panel — immersive acoustic 3D stage */}
      <div ref={stageRef} className="hidden lg:flex flex-col items-center justify-center p-12 acoustic-hex-bg relative overflow-hidden border-r border-slate-200 acoustic-stage">
        {/* Real interior backdrop — luxury room with UniVicoustic panels (heavily lightened) */}
        <div className="interior-backdrop" style={{
          backgroundImage: "linear-gradient(to right, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.7) 60%, rgba(255,255,255,0.9) 100%), url('/uv-interior-1.png')"
        }} />

        {/* Scanning sweep + corner wedge panels for 3D depth */}
        <div className="scan-sweep" />
        <div className="wedge-panel px-1" style={{ top: "-60px", left: "-60px" }} />
        <div className="wedge-panel px-1" style={{ bottom: "-60px", right: "-60px", transform: "rotate(165deg)" }} />

        {/* Floating accent dots */}
        <div className="float-dot" style={{ top: "20%", left: "15%", animationDelay: "0s" }} />
        <div className="float-dot" style={{ top: "70%", left: "82%", animationDelay: "2s", background: "#222222", boxShadow: "0 0 10px rgba(34,34,34,0.4)" }} />
        <div className="float-dot" style={{ top: "82%", left: "18%", animationDelay: "4s" }} />
        <div className="float-dot" style={{ top: "12%", left: "78%", animationDelay: "5.5s", background: "#222222", boxShadow: "0 0 10px rgba(34,34,34,0.4)" }} />

        {/* Continuous sound rings emanating from logo center (parallax) */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none px-2">
          <div className="relative w-1 h-1">
            <span className="sound-ring" style={{ width: 200, height: 200, animationDelay: "0s" }} />
            <span className="sound-ring sound-ring--alt" style={{ width: 200, height: 200, animationDelay: "0.7s" }} />
            <span className="sound-ring" style={{ width: 200, height: 200, animationDelay: "1.4s" }} />
            <span className="sound-ring sound-ring--alt" style={{ width: 200, height: 200, animationDelay: "2.1s" }} />
            <span className="sound-ring" style={{ width: 200, height: 200, animationDelay: "2.8s" }} />
            <span className="sound-ring sound-ring--alt" style={{ width: 200, height: 200, animationDelay: "3.5s" }} />
          </div>
        </div>

        {/* Main content — logo tilts with cursor, text floats subtly */}
        <div className="relative text-center max-w-lg z-10 px-logo">
          <div className="logo-3d mb-10 inline-block">
            <Logo className="h-44 md:h-52 mx-auto" />
          </div>
          <h1 className="fade-up fade-up-1 text-3xl md:text-4xl font-display font-extrabold tracking-tight text-[#222222] leading-tight">
            Welcome to
          </h1>
          <h2 className="fade-up fade-up-2 text-4xl md:text-5xl font-display font-extrabold tracking-tight text-[#E53935] leading-tight mt-2">
            UniVicoustic <br/>Learning Academy
          </h2>
          <div className="fade-up fade-up-3 mt-8 inline-flex items-center gap-3 px-4 py-2 bg-white/85 backdrop-blur-md border border-slate-200 rounded-full shadow-sm">
            <div className="eq-bars" style={{ height: 18 }}>
              {Array.from({ length: 12 }).map((_, i) => <div key={i} className="eq-bar"/>)}
            </div>
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#222222] font-semibold">Makes Sound Sense</span>
          </div>
        </div>

        {/* Bottom-right corner equalizer */}
        <div className="absolute bottom-10 right-10 opacity-40 px-1">
          <div className="eq-bars">
            {Array.from({ length: 8 }).map((_, i) => <div key={i} className="eq-bar"/>)}
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-slate-50">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8"><Logo /></div>

          <h2 className="text-3xl font-display font-bold text-slate-900 mb-2">Sign in</h2>
          <p className="text-slate-500 mb-8 text-sm">Choose your access role to continue.</p>

          <div className="grid grid-cols-3 gap-2 mb-6 p-1 bg-white border border-slate-200 rounded-lg">
            <button
              data-testid="tab-admin"
              onClick={() => setTab("admin")}
              className={`flex items-center justify-center gap-2 py-2.5 text-sm font-medium rounded-md transition-colors ${
                tab === "admin" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              <ShieldCheck size={16} /> Admin
            </button>
            <button
              data-testid="tab-learner"
              onClick={() => setTab("learner")}
              className={`flex items-center justify-center gap-2 py-2.5 text-sm font-medium rounded-md transition-colors ${
                tab === "learner" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              <GraduationCap size={16} /> Learner
            </button>
            <button
              data-testid="tab-dealer"
              onClick={() => setTab("dealer")}
              className={`flex items-center justify-center gap-2 py-2.5 text-sm font-medium rounded-md transition-colors ${
                tab === "dealer" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              <Handshake size={16} /> Dealer
            </button>
          </div>

          {/* Admin password form */}
          {tab === "admin" && (
            <form onSubmit={onAdminSubmit} className="space-y-4" data-testid="admin-login-form">
              <div>
                <Label htmlFor="email" className="text-slate-700 text-sm">Email</Label>
                <Input
                  id="email"
                  data-testid="login-email-input"
                  type="email"
                  placeholder="admin@univicoustic.com"
                  value={adminEmail}
                  onChange={(e) => setAdminEmail(e.target.value)}
                  required
                  className="mt-1.5 bg-white"
                />
              </div>
              <div>
                <Label htmlFor="password" className="text-slate-700 text-sm">Password</Label>
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type="password"
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  required
                  className="mt-1.5 bg-white"
                />
              </div>
              <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="admin-remember-me">
                <Checkbox checked={rememberMe} onCheckedChange={(v) => setRememberMe(!!v)} />
                <span className="text-sm text-slate-600">Keep me signed in for 30 days</span>
              </label>
              <Button
                type="submit"
                data-testid="login-submit-button"
                disabled={loading}
                className="w-full bg-[#222222] hover:bg-black text-white h-11 group"
              >
                {loading ? "Signing in..." : (<>Sign in <ArrowRight size={16} className="ml-2 group-hover:translate-x-0.5 transition-transform" /></>)}
              </Button>
            </form>
          )}

          {/* Dealer sign in (email + password, pre-provisioned accounts) */}
          {tab === "dealer" && (
            <form onSubmit={onDealerSubmit} className="space-y-4" data-testid="dealer-login-form">
              <div>
                <Label htmlFor="dealer-email" className="text-slate-700 text-sm">Email</Label>
                <div className="relative mt-1.5">
                  <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    id="dealer-email"
                    data-testid="dealer-email-input"
                    type="email"
                    placeholder="you@yourcompany.com"
                    value={dealerEmail}
                    onChange={(e) => setDealerEmail(e.target.value)}
                    required
                    className="bg-white pl-9"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="dealer-password" className="text-slate-700 text-sm">Password</Label>
                <div className="relative mt-1.5">
                  <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    id="dealer-password"
                    data-testid="dealer-password-input"
                    type={showPassword ? "text" : "password"}
                    value={dealerPassword}
                    onChange={(e) => setDealerPassword(e.target.value)}
                    required
                    className="bg-white pl-9 pr-10"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
                <p className="text-[11px] text-slate-500 mt-1.5" data-testid="dealer-login-note">
                  Dealer accounts are set up by UniVicoustic. First time here? Sign in with the
                  password shared with you — you'll be asked to set your own.
                </p>
              </div>
              <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="dealer-remember-me">
                <Checkbox checked={rememberMe} onCheckedChange={(v) => setRememberMe(!!v)} />
                <span className="text-sm text-slate-600">Keep me signed in for 30 days</span>
              </label>
              <Button
                type="submit"
                data-testid="dealer-submit-button"
                disabled={loading}
                className="w-full bg-[#222222] hover:bg-black text-white h-11 group"
              >
                {loading ? "Signing in..." : (<>Sign in <ArrowRight size={16} className="ml-2 group-hover:translate-x-0.5 transition-transform" /></>)}
              </Button>
            </form>
          )}

          {/* Learner sign in / sign up (email + password) */}
          {tab === "learner" && (
            <form onSubmit={onLearnerSubmit} className="space-y-4" data-testid="learner-auth-form">
              <div className="grid grid-cols-2 gap-1 p-1 bg-slate-100 border border-slate-200 rounded-lg" data-testid="learner-mode-toggle">
                <button
                  type="button"
                  data-testid="learner-signin-toggle"
                  onClick={() => setLearnerMode("signin")}
                  className={`py-2 text-xs font-semibold uppercase tracking-wider rounded-md transition-colors ${
                    learnerMode === "signin" ? "bg-white text-[#222222] shadow-sm" : "text-slate-500 hover:text-slate-700"
                  }`}
                >
                  Sign in
                </button>
                <button
                  type="button"
                  data-testid="learner-signup-toggle"
                  onClick={() => setLearnerMode("signup")}
                  className={`py-2 text-xs font-semibold uppercase tracking-wider rounded-md transition-colors ${
                    learnerMode === "signup" ? "bg-white text-[#E53935] shadow-sm" : "text-slate-500 hover:text-slate-700"
                  }`}
                >
                  Sign up
                </button>
              </div>

              {learnerMode === "signup" && (
                <>
                  <div>
                    <Label htmlFor="learner-name" className="text-slate-700 text-sm">Full name</Label>
                    <div className="relative mt-1.5">
                      <UserRound size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                      <Input
                        id="learner-name"
                        data-testid="learner-name-input"
                        type="text"
                        placeholder="Alex Morgan"
                        value={learnerName}
                        onChange={(e) => setLearnerName(e.target.value)}
                        required
                        className="bg-white pl-9"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="learner-dept" className="text-slate-700 text-sm">Department / team</Label>
                    <div className="relative mt-1.5">
                      <Building2 size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                      <Input
                        id="learner-dept"
                        data-testid="learner-department-input"
                        type="text"
                        placeholder="Sales — North"
                        value={learnerDept}
                        onChange={(e) => setLearnerDept(e.target.value)}
                        className="bg-white pl-9"
                      />
                    </div>
                  </div>
                </>
              )}

              <div>
                <Label htmlFor="learner-email" className="text-slate-700 text-sm">Work email</Label>
                <div className="relative mt-1.5">
                  <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  <Input
                    id="learner-email"
                    data-testid="learner-email-input"
                    type="email"
                    placeholder={learnerMode === "signup" ? "you@united-group.in" : "you@company.com"}
                    value={learnerEmail}
                    onChange={(e) => setLearnerEmail(e.target.value)}
                    required
                    className="bg-white pl-9"
                  />
                </div>
                {learnerMode === "signup" && (
                  <p className="text-[11px] text-slate-500 mt-1.5" data-testid="signup-domain-note">
                    Sign-up is open to <strong>@united-group.in</strong> email addresses.
                    Outside United Group? Request access at{" "}
                    <a href="mailto:aiteam@united-group.in" className="text-[#E53935] font-medium hover:underline">aiteam@united-group.in</a>.
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="learner-password" className="text-slate-700 text-sm">Password</Label>
                <div className="relative mt-1.5">
                  <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  <Input
                    id="learner-password"
                    data-testid="learner-password-input"
                    type={showPassword ? "text" : "password"}
                    placeholder={learnerMode === "signup" ? "Min. 8 characters" : "Your password"}
                    value={learnerPassword}
                    onChange={(e) => setLearnerPassword(e.target.value)}
                    required
                    minLength={learnerMode === "signup" ? 8 : undefined}
                    className="bg-white pl-9 pr-10"
                  />
                  <button
                    type="button"
                    data-testid="toggle-password-visibility"
                    onClick={() => setShowPassword((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
                {learnerMode === "signup" && (
                  <p className="text-[11px] text-slate-500 mt-1.5">At least 8 characters. You&apos;ll use this to sign in from now on.</p>
                )}
              </div>
              {learnerMode === "signin" && (
                <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="learner-remember-me">
                  <Checkbox checked={rememberMe} onCheckedChange={(v) => setRememberMe(!!v)} />
                  <span className="text-sm text-slate-600">Keep me signed in for 30 days</span>
                </label>
              )}
              <Button
                type="submit"
                data-testid="learner-submit-button"
                disabled={loading}
                className="w-full bg-[#E53935] hover:bg-[#c62828] text-white h-11 group"
              >
                {loading
                  ? (learnerMode === "signup" ? "Creating account..." : "Signing in...")
                  : (<>{learnerMode === "signup" ? "Create my account" : "Sign in"} <ArrowRight size={16} className="ml-2 group-hover:translate-x-0.5 transition-transform" /></>)}
              </Button>
              <p className="text-xs text-slate-500 text-center">
                {learnerMode === "signin" ? (
                  <>New to the academy?{" "}
                    <button type="button" data-testid="switch-to-signup-link" onClick={() => setLearnerMode("signup")} className="text-[#E53935] font-medium hover:underline">Sign up</button>
                  </>
                ) : (
                  <>Already have an account?{" "}
                    <button type="button" data-testid="switch-to-signin-link" onClick={() => setLearnerMode("signin")} className="text-[#E53935] font-medium hover:underline">Sign in</button>
                  </>
                )}
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
